/**
 * Natural alphanumeric comparison for device names.
 *
 * - Case-insensitive
 * - Text portions sort alphabetically
 * - Numeric portions sort by numeric value (M-2 < M-08 < M-10 < M-15)
 * - Hyphen/underscore/space segments keep device groups together
 * - Pure-letter group codes (LL, M) sort before letter+digit codes (L02)
 *   so groups like Revera-LL-*, Revera-M-*, Revera-L02-* stay contiguous
 *   in a sensible building-floor order.
 *
 * Display-only — does not mutate names or database records.
 */

function classifySegment(seg) {
  const s = String(seg ?? "");
  if (/^\d+$/.test(s)) {
    return { kind: "num", n: Number.parseInt(s, 10), t: "" };
  }
  if (/^[a-zA-Z]+$/.test(s)) {
    return { kind: "alpha", n: 0, t: s.toLowerCase() };
  }
  const mixed = s.match(/^([a-zA-Z]+)(\d+)$/);
  if (mixed) {
    return {
      kind: "mixed",
      n: Number.parseInt(mixed[2], 10),
      t: mixed[1].toLowerCase(),
    };
  }
  return { kind: "other", n: 0, t: s.toLowerCase() };
}

function compareSegments(a, b) {
  const ca = classifySegment(a);
  const cb = classifySegment(b);

  // Letter-only group codes before letter+digit (LL, M before L02).
  if (ca.kind === "alpha" && cb.kind === "mixed") return -1;
  if (ca.kind === "mixed" && cb.kind === "alpha") return 1;

  const kindOrder = { num: 0, alpha: 1, mixed: 2, other: 3 };
  if (ca.kind !== cb.kind) {
    return kindOrder[ca.kind] - kindOrder[cb.kind];
  }

  if (ca.kind === "num") return ca.n - cb.n;

  if (ca.kind === "alpha") {
    return ca.t.localeCompare(cb.t);
  }

  if (ca.kind === "mixed") {
    const textCmp = ca.t.localeCompare(cb.t);
    if (textCmp !== 0) return textCmp;
    return ca.n - cb.n;
  }

  return ca.t.localeCompare(cb.t);
}

function splitNameParts(name) {
  return String(name ?? "")
    .trim()
    .split(/[-_\s]+/)
    .filter(Boolean);
}

/**
 * Compare two device display names in natural alphanumeric order.
 * @returns {number} negative if a < b, positive if a > b, 0 if equal
 */
export function compareNaturalDeviceName(a, b) {
  const sa = String(a ?? "");
  const sb = String(b ?? "");
  const pa = splitNameParts(sa);
  const pb = splitNameParts(sb);
  const len = Math.max(pa.length, pb.length);

  for (let i = 0; i < len; i += 1) {
    if (i >= pa.length) return -1;
    if (i >= pb.length) return 1;
    const cmp = compareSegments(pa[i], pb[i]);
    if (cmp !== 0) return cmp;
  }

  return sa.localeCompare(sb, undefined, { sensitivity: "base", numeric: true });
}

/** Display label for sorting: prefer name, fall back to DevEUI. */
export function deviceSortLabel(device) {
  const name = device?.name != null ? String(device.name).trim() : "";
  if (name) return name;
  return String(device?.devEui || device?.id || "");
}

/**
 * Sort a device list by natural name (does not mutate the input).
 * @param {Array} devices
 * @returns {Array}
 */
export function sortDevicesByNaturalName(devices) {
  return [...(devices || [])].sort((a, b) =>
    compareNaturalDeviceName(deviceSortLabel(a), deviceSortLabel(b))
  );
}
